import type React from 'react';
import type { ReactNode } from 'react';
import type { Chart as ChartJS } from 'chart.js';

import ChartActionsMenu from '@/components/molecules/ChartActionsMenu';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { DateRangePresetId } from '@/components/molecules/DateRangePicker/DateRangePresets';

import styles from './ChartHeaderActions.module.scss';

export interface ChartHeaderActionsProps {
    chartRef: React.RefObject<ChartJS<'line' | 'bar'> | null>;
    chartName: string;
    isFullScreen: boolean;
    disabled?: boolean;
    /**
     * Disables only the chart actions menu, for a chart with nothing to export. The date
     * range stays usable, since picking another range is the way out of an empty one.
     */
    menuDisabled?: boolean;
    /** Defaults to `true`; see `ChartActionsMenuProps.csvExport`. */
    csvExport?: boolean;
    onFullScreenToggle: () => void;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (data: DateRange) => void;
    defaultPresetId?: DateRangePresetId;
    show24hPreset?: boolean;
    showLast3DaysPreset?: boolean;
    hiddenPresetIds?: readonly DateRangePresetId[];
    /**
     * Defaults to `true`. Set `false` on pages where several charts share one
     * range and the picker lives at page level — otherwise each chart would render
     * its own copy of the same control.
     */
    showDateRangePicker?: boolean;
    /** Takes the date range's place, for a chart with its own period control. */
    picker?: ReactNode;
}

export default function ChartHeaderActions(props: ChartHeaderActionsProps) {
    const {
        chartRef,
        chartName,
        isFullScreen,
        disabled = false,
        menuDisabled = false,
        csvExport = true,
        onFullScreenToggle,
        dateFrom,
        dateTo,
        onDateChange,
        defaultPresetId = 'last7days',
        show24hPreset = false,
        showLast3DaysPreset = false,
        hiddenPresetIds,
        showDateRangePicker = true,
        picker,
    } = props;

    return (
        <div className={styles.headerActions}>
            {picker}
            {picker === undefined && showDateRangePicker && (
                <DateRangePicker
                    disabled={disabled}
                    menuPosition="right"
                    defaultPresetId={defaultPresetId}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    show24hPreset={show24hPreset}
                    showLast3DaysPreset={showLast3DaysPreset}
                    hiddenPresetIds={hiddenPresetIds}
                />
            )}
            <ChartActionsMenu
                disabled={disabled || menuDisabled}
                chartRef={chartRef}
                chartName={chartName}
                isFullScreen={isFullScreen}
                onFullScreenToggle={onFullScreenToggle}
                csvExport={csvExport}
            />
        </div>
    );
}
